// app/articles/ArticlesClient.tsx
'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowRight, PlusCircle, Calendar, Search, 
  Bookmark, BookmarkCheck, FileQuestion, X, Edit3, Trash2, Globe
} from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useLocale } from '@/i18n/context';
import { SUPPORTED_ARTICLE_LANGS, parseArticleMeta, isArticleAuthor, removeAuthoredArticle, ArticleData } from '@/lib/article-meta';
import { useToast } from '@/components/ui/Toast';
import { useRouter } from 'next/navigation';

interface Props {
  initialArticles: ArticleData[]; // تایپ دیتای دریافتی از سرور
}

export default function ArticlesClient({ initialArticles }: Props) {
  const { t, isRtl, locale } = useLocale();
  const [isAdmin, setIsAdmin] = useState(false);
  const [sessionUser, setSessionUser] = useState<unknown>(null);
  const [filterLang, setFilterLang] = useState<string>('all');
  
  // استیت اولیه مستقیماً با دیتای سرور پر میشه (بدون لودینگ کلاینت)
  const [articles, setArticles] = useState<ArticleData[]>(initialArticles.map(parseArticleMeta));
  
  const { toast } = useToast();
  const router = useRouter();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [showBookmarks, setShowBookmarks] = useState(false);
  const [bookmarkedSlugs, setBookmarkedSlugs] = useState<string[]>([]);

  const supabase = createClient();

  // گرفتن سشن یوزر برای دکمه‌های ادمین و ویرایش
  useEffect(() => {
    const initAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setSessionUser(session?.user || null);
      if (session?.user) {
        setIsAdmin(true);
      }
    };
    initAuth();

    // اتصال ریل‌تایم: اگر کسی مقاله جدیدی گذاشت، کش سرور رو رفرش کن
    const channel = supabase.channel('realtime:articles_list')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'articles' }, () => {
        router.refresh(); 
      })
      .subscribe();

    const saved = localStorage.getItem('bookmarked_articles');
    if (saved) {
      try { 
        setBookmarkedSlugs(JSON.parse(saved));
      } catch (e) {}
    }

    return () => {
      supabase.removeChannel(channel);
    };
  }, [supabase, router]);

  // وقتی سرور دیتای جدیدی پاس داد (به خاطر router.refresh)، استیت کلاینت آپدیت بشه
  useEffect(() => {
    setArticles(initialArticles.map(parseArticleMeta));
  }, [initialArticles]);

  const filteredArticles = articles.filter(article => {
    const title = article.title || '';
    const excerpt = article.excerpt || '';
    const slug = article.slug || '';
    const matchesSearch = title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          (excerpt && excerpt.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesBookmark = showBookmarks ? bookmarkedSlugs.includes(slug) : true;
    const matchesLang = filterLang === 'all' || article.lang === filterLang;
    return matchesSearch && matchesBookmark && matchesLang;
  });

  const handleDeleteFromList = async (e: React.MouseEvent, id: string, slug: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm(t.articles.confirmDeleteMsg)) return;
    
    const { error } = await supabase.from('articles').delete().eq('id', id);
    if (!error) {
      removeAuthoredArticle(slug);
      removeAuthoredArticle(String(id));
      setArticles(prev => prev.filter(a => a.id !== id));
      toast.success(t.articles.deleteSuccess, t.articles.deleteSuccess);
      router.refresh();
    } else {
      toast.error(t.common.error, error.message);
    }
  };

  const toggleBookmark = (e: React.MouseEvent, slug: string) => {
    e.preventDefault();
    try {
      let saved = JSON.parse(localStorage.getItem('bookmarked_articles') || '[]');
      if (saved.includes(slug)) {
        saved = saved.filter((s: string) => s !== slug);
        setBookmarkedSlugs(prev => prev.filter(s => s !== slug));
      } else {
        saved.push(slug);
        setBookmarkedSlugs(prev => [...prev, slug]);
      }
      localStorage.setItem('bookmarked_articles', JSON.stringify(saved));
    } catch(err) {}
  };

  const clearFilters = () => {
    setSearchTerm('');
    setShowBookmarks(false);
  };

  const formatArticleDate = (dateStr: string) => {
    const d = new Date(dateStr);
    if (locale === 'fa') return d.toLocaleDateString('fa-IR');
    if (locale === 'ar') return d.toLocaleDateString('ar-EG');
    if (locale === 'tr') return d.toLocaleDateString('tr-TR');
    return d.toLocaleDateString('en-US');
  };

  return (
    <div className="min-h-screen bg-[#F5F0E8] dark:bg-zinc-950 pt-10 md:pt-16 pb-16 px-4 sm:px-6 lg:px-8 selection:bg-[#206A4F]/20 dark:selection:bg-emerald-500/30 overflow-x-hidden transition-colors duration-300" dir={isRtl ? "rtl" : "ltr"}>
      <div className="max-w-6xl mx-auto">
        
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 sm:mb-12 gap-5">
          <div>
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ type: "spring", stiffness: 120 }}
              className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#206A4F] dark:text-white mb-2 sm:mb-4"
            >
              {t.articles.title}
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="text-[#206A4F]/80 dark:text-zinc-300 text-base sm:text-lg font-medium"
            >
              {t.articles.subtitle}
            </motion.p>
          </div>

          <AnimatePresence>
            {isAdmin && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <Link href="/articles/create" passHref>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex items-center gap-2.5 bg-[#206A4F] dark:bg-emerald-500 hover:bg-[#18533E] dark:hover:bg-emerald-400 text-[#F5F0E8] dark:text-black px-7 py-3.5 rounded-2xl font-black shadow-lg hover:shadow-xl hover:shadow-[#206A4F]/30 dark:hover:shadow-none hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 cursor-pointer"
                  >
                    <PlusCircle size={20} />
                    <span>{t.admin.newArticleBtn}</span>
                  </motion.button>
                </Link>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Search & Tabs Section */}
        <div className="flex flex-col md:flex-row gap-4 mb-10 items-center justify-between">
          <div className="relative flex bg-white/70 dark:bg-zinc-900/80 p-1.5 rounded-2xl border border-[#206A4F]/10 dark:border-zinc-800 w-full md:w-auto shadow-sm transition-colors duration-300">
            {[
              { id: 'all', label: t.articles.allTab, icon: null },
              { id: 'bookmarks', label: t.articles.bookmarksTab, icon: <Bookmark size={16} /> }
            ].map((tab) => {
              const isActive = (tab.id === 'bookmarks') === showBookmarks;
              return (
                <button
                  key={tab.id}
                  onClick={() => setShowBookmarks(tab.id === 'bookmarks')}
                  className={`relative flex-1 md:flex-none px-6 py-2.5 rounded-xl font-bold text-sm transition-all duration-200 hover:scale-105 active:scale-95 flex items-center justify-center gap-2 z-10 cursor-pointer ${
                    isActive ? 'text-[#F5F0E8] dark:text-black font-black shadow-sm' : 'text-[#206A4F]/70 dark:text-zinc-400 hover:text-[#206A4F] dark:hover:text-emerald-400 hover:bg-black/5 dark:hover:bg-white/5'
                  }`}
                >
                  {isActive && (
                    <motion.div
                      layoutId="active-tab"
                      className="absolute inset-0 bg-[#206A4F] dark:bg-emerald-500 rounded-xl -z-10 shadow-md"
                      transition={{ type: "spring", stiffness: 400, damping: 30 }}
                    />
                  )}
                  {tab.icon} {tab.label}
                </button>
              );
            })}
          </div>

          <div className="relative w-full md:w-96 group">
            <div className="absolute inset-y-0 rtl:left-auto rtl:right-0 ltr:right-auto ltr:left-0 rtl:pr-4 ltr:pl-4 flex items-center pointer-events-none text-[#206A4F]/40 dark:text-zinc-500 group-focus-within:text-[#206A4F] dark:group-focus-within:text-emerald-400 transition-colors">
              <Search size={18} />
            </div>
            <input 
              type="text"
              placeholder={t.articles.searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white dark:bg-zinc-900 border border-[#206A4F]/10 dark:border-zinc-800 text-[#206A4F] dark:text-white px-12 py-3.5 rounded-2xl focus:outline-none focus:ring-4 focus:ring-[#206A4F]/10 dark:focus:ring-emerald-500/20 focus:border-[#206A4F]/30 dark:focus:border-emerald-500/40 transition-all font-bold shadow-sm placeholder:text-[#206A4F]/40 dark:placeholder:text-zinc-500 placeholder:font-medium"
            />
            <AnimatePresence>
              {searchTerm && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.5 }}
                  onClick={() => setSearchTerm('')}
                  className="absolute inset-y-0 rtl:left-0 rtl:right-auto ltr:right-0 ltr:left-auto rtl:pl-4 ltr:pr-4 flex items-center text-[#206A4F]/40 dark:text-zinc-500 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                >
                  <X size={18} />
                </motion.button>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-8 bg-white/50 dark:bg-zinc-900/50 p-2.5 rounded-2xl border border-[#206A4F]/10 dark:border-zinc-800/80 backdrop-blur-md">
          <span className="text-xs font-black px-2 text-[#206A4F] dark:text-emerald-400 flex items-center gap-1.5">
            <Globe size={15} />
            <span>{t.articles.writtenIn}:</span>
          </span>
          <button
            onClick={() => setFilterLang('all')}
            className={`px-4 py-1.5 rounded-xl font-bold text-xs transition-all cursor-pointer ${
              filterLang === 'all'
                ? 'bg-[#206A4F] dark:bg-emerald-500 text-white dark:text-black font-black shadow-xs'
                : 'hover:bg-black/5 dark:hover:bg-white/5 text-[#206A4F]/75 dark:text-zinc-300'
            }`}
          >
            {t.articles.langFilterAll}
          </button>
          {SUPPORTED_ARTICLE_LANGS.map((lng) => (
            <button
              key={lng.id}
              onClick={() => setFilterLang(lng.id)}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl font-bold text-xs transition-all cursor-pointer ${
                filterLang === lng.id
                  ? 'bg-[#206A4F] dark:bg-emerald-500 text-white dark:text-black font-black shadow-xs scale-105'
                  : 'hover:bg-black/5 dark:hover:bg-white/5 text-[#206A4F]/75 dark:text-zinc-300'
              }`}
            >
              <span className="text-sm">{lng.flag}</span>
              <span>{lng.label}</span>
            </button>
          ))}
        </div>

        {/* Content Area */}
        {filteredArticles.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center text-center py-24 bg-white/50 dark:bg-zinc-900/60 border border-[#206A4F]/10 dark:border-zinc-800 rounded-3xl transition-colors duration-300"
          >
            <div className="w-20 h-20 bg-[#206A4F]/5 dark:bg-emerald-500/15 rounded-full flex items-center justify-center text-[#206A4F]/40 dark:text-emerald-400 mb-6 border border-[#206A4F]/10 dark:border-emerald-500/30">
              <FileQuestion size={40} className="animate-bounce" />
            </div>
            <p className="text-2xl font-black text-[#206A4F] dark:text-white mb-2">{t.articles.noArticles}</p>
            <p className="text-[#206A4F]/70 dark:text-zinc-400 mb-6 max-w-md font-medium">
              {showBookmarks ? t.articles.noBookmarks : t.articles.noArticles}
            </p>
            {(searchTerm || showBookmarks) && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={clearFilters}
                className="px-6 py-3 bg-[#206A4F]/15 dark:bg-emerald-500/20 text-[#206A4F] dark:text-emerald-300 rounded-full font-extrabold hover:bg-[#206A4F]/25 dark:hover:bg-emerald-500/30 transition-colors border border-[#206A4F]/20 dark:border-emerald-500/30 shadow-sm"
              >
                {t.articles.clearFilters}
              </motion.button>
            )}
          </motion.div>
        ) : (
          <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
            <AnimatePresence mode="popLayout">
              {filteredArticles.map((article, index) => (
                <motion.article
                  layout
                  initial={{ opacity: 0, scale: 0.9, filter: "blur(5px)" }}
                  animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                  exit={{ opacity: 0, scale: 0.9, filter: "blur(5px)" }}
                  transition={{ type: "spring", stiffness: 250, damping: 25 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  key={article.id || article.slug}
                  className={`relative group bg-white dark:bg-zinc-900/90 rounded-3xl border border-[#206A4F]/10 dark:border-zinc-800 hover:border-[#206A4F]/30 dark:hover:border-emerald-500/50 transition-all duration-300 hover:shadow-2xl hover:shadow-[#206A4F]/10 dark:hover:shadow-emerald-950/40 overflow-hidden flex flex-col ${
                    index === 0 ? 'md:col-span-2 lg:col-span-2 row-span-2' : 'col-span-1'
                  }`}
                >
                  <Link 
                    href={`/articles/${article.id}`} 
                    className="absolute inset-0 z-10"
                    aria-label={`${t.articles.readMore}: ${article.title}`}
                  />

                  <div className="p-5 sm:p-6 md:p-8 flex flex-col h-full relative z-20 pointer-events-none">
                    <div className="flex items-center justify-between text-xs sm:text-sm text-[#206A4F]/70 dark:text-zinc-400 mb-4 sm:mb-6 pointer-events-auto">
                      <span className="flex items-center gap-1.5 font-bold text-xs bg-[#206A4F]/5 dark:bg-zinc-800 px-3 py-1.5 rounded-xl border border-[#206A4F]/10 dark:border-zinc-700">
                        <Calendar size={15} className="text-emerald-600 dark:text-emerald-400" /> 
                        {formatArticleDate(article.created_at || '')}
                      </span>
                      <div className="flex items-center gap-2">
                        {article?.languageInfo && (
                          <span className="bg-emerald-500/10 text-[#206A4F] dark:text-emerald-300 px-2.5 py-1 rounded-xl font-extrabold text-xs border border-emerald-500/20 flex items-center gap-1">
                            <span>{article.languageInfo.flag}</span>
                            <span>{article.languageInfo.label}</span>
                          </span>
                        )}
                        {bookmarkedSlugs.includes(article.slug || '') && (
                          <motion.span 
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="bg-[#206A4F]/10 dark:bg-emerald-500/20 text-[#206A4F] dark:text-emerald-300 px-3 py-1 rounded-full flex items-center gap-1.5 text-xs font-bold border border-[#206A4F]/20 dark:border-emerald-500/30"
                          >
                            <BookmarkCheck size={14} className="text-emerald-500" /> {t.articles.bookmark}
                          </motion.span>
                        )}
                        {isArticleAuthor(article, sessionUser) && (
                          <div className="flex items-center gap-1 z-30 pointer-events-auto">
                            <button
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); router.push(`/articles/edit/${article.id}`); }}
                              className="p-1.5 rounded-lg bg-emerald-600/10 hover:bg-emerald-600 text-emerald-600 hover:text-white dark:text-emerald-400 transition-colors cursor-pointer"
                              title={t.articles.editArticle}
                            >
                              <Edit3 size={15} />
                            </button>
                            <button
                              onClick={(e) => handleDeleteFromList(e, String(article.id), article.slug || '')}
                              className="p-1.5 rounded-lg bg-rose-500/10 hover:bg-rose-500 text-rose-500 hover:text-white transition-colors cursor-pointer"
                              title={t.articles.deleteArticle}
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <h2 className={`${index === 0 ? 'text-2xl sm:text-3xl md:text-4xl' : 'text-xl sm:text-2xl'} font-black text-[#206A4F] dark:text-white mb-3 sm:mb-4 group-hover:text-[#18533E] dark:group-hover:text-emerald-400 transition-colors leading-tight`}>
                      {article.title}
                    </h2>
                    
                    <p className={`text-[#206A4F]/80 dark:text-zinc-300 leading-relaxed font-medium mb-6 sm:mb-8 flex-1 ${index === 0 ? 'text-base sm:text-lg' : 'text-sm sm:text-base line-clamp-3'}`}>
                      {article.excerpt || `${t.articles.readMore}...`}
                    </p>

                    <div className="mt-auto flex items-center justify-between border-t border-[#206A4F]/10 dark:border-zinc-800 pt-6 pointer-events-auto">
                      <motion.button 
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={(e: React.MouseEvent) => toggleBookmark(e, article.slug || '')}
                        className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 z-30 relative cursor-pointer ${
                          bookmarkedSlugs.includes(article.slug || '') 
                            ? 'bg-[#206A4F] dark:bg-emerald-500 text-[#F5F0E8] dark:text-black shadow-md shadow-[#206A4F]/25 dark:shadow-emerald-500/30' 
                            : 'bg-[#F5F0E8]/70 dark:bg-zinc-800 border border-[#206A4F]/15 dark:border-zinc-700 text-[#206A4F] dark:text-zinc-300 hover:bg-[#206A4F] hover:text-white dark:hover:bg-zinc-700 dark:hover:text-white'
                        }`}
                        title={t.articles.bookmark}
                      >
                        {bookmarkedSlugs.includes(article.slug || '') ? <BookmarkCheck size={20} /> : <Bookmark size={20} />}
                      </motion.button>

                      <div className="pointer-events-none flex items-center gap-2 text-sm font-black text-[#206A4F] dark:text-emerald-400 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform">
                        <span>{t.articles.readMore}</span>
                        <div className="w-10 h-10 rounded-full bg-[#206A4F]/10 dark:bg-emerald-500/20 flex items-center justify-center text-[#206A4F] dark:text-emerald-400 group-hover:bg-[#206A4F] group-hover:text-[#F5F0E8] dark:group-hover:bg-emerald-400 dark:group-hover:text-black transition-colors z-20">
                          <ArrowRight size={18} className="rtl:rotate-180" />
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.article>
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </div>
    </div>
  );
}